import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { App, Flex, Space, Typography } from "antd"
import { connect, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import Filters from "../../../Components/Filters"
import GlobalFilterTestData from "../../../Components/GlobalFilterTestData/globalFilterTest"
import ManageColumn from "../../../Components/ManageColumns"
import Table from "../../../Components/Table"
import { getBaseColumns } from "../../../Components/Table/baseColumns"
import Icon from "../../../Icon"
import { serializeData } from "../../../Utils/utils"
import { useUpdatePreferences } from "../../User/hooks/useUpdatePreferences"
import { selectRequestResult } from "../reducer"
import { getActionColumn, getColumns, getDefaultColumn } from "./components/Table/columns"
import {
  REQUESTS_GIFTS_PREFERENCES_VERSION,
  requestsGiftsActions,
  selectAreFiltersDefault
} from "./redux/requestsGifts.slice"
import { getRequestsGifts } from "./redux/requestsGifts.thunks"

const pageSize = 50

const { Text } = Typography

const pinnedColumns = ["Created date"]

const RequestsGiftsPage = ({
  isMobile,
  businessAccountId,
  visibleFiltersAdmin,
  defaultColumnsAdmin,
  columnsAdmin,
  fetching,
  result,
  filters,
  statistics,
  test_data,
  ...props
}) => {
  const navigate = useNavigate()
  const { notification, modal } = App.useApp()

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)

  const isFiltersDefault = useSelector(selectAreFiltersDefault)
  const requestResult = useSelector(selectRequestResult)

  const { count, results } = result

  const myColumns = columnsAdmin
  const allFilters = defaultColumnsAdmin
  const visibleFilters = visibleFiltersAdmin

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")

  const preferencesData = useMemo(
    () => ({
      version: REQUESTS_GIFTS_PREFERENCES_VERSION,
      filters,
      columnsAdmin,
      visibleFiltersAdmin
    }),
    [filters, columnsAdmin, visibleFiltersAdmin]
  )
  useUpdatePreferences("requests-gifts", preferencesData)

  const statisticsFolder = statistics?.["integrations_counter"] || 0

  const notificationConfig = {
    closeIcon: <Text>Dismiss</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  useEffect(() => {
    document.title = "Requests Integrations - Chargeback"
  }, [])

  const prevTestDataRef = useRef(test_data)

  useEffect(() => {
    if (requestResult) return
    if (requestResult) return

    const testDataChanged = prevTestDataRef.current !== test_data
    if (testDataChanged) {
      prevTestDataRef.current = test_data
      if (pageNumber !== 1) {
        setPageNumber(1)
        return
      }
    }

    const serializeFilters = serializeData(filters)
    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      ...(columns.length && { columns: ["created_timestamp", "id", ...columns].join(",") }),
      test_data
    }
    props.getInfo({ ...data })
  }, [businessAccountId, ordering, pageNumber, filters, test_data, myColumnsLength, requestResult])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        //x: (230 * myColumns.filter(({active}) => active).length),
        // x: count ? 'min-content' : (230 * myColumns.filter(({active}) => active).length),
        x: myColumns.filter(({ active }) => active).length > 3 ? "max-content" : "auto",
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px) - 16px)",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      },
      locale: {
        emptyText: !statisticsFolder ? (
          <EmptyComponent type="requests_integrations_admin" />
        ) : (
          <EmptyComponent type="search">
            <Button
              className="flex"
              icon={<Icon type={"menu_search"} />}
              onClick={props.clearFilters}
            >
              Clear search
            </Button>
          </EmptyComponent>
        )
      }
    }),
    [myColumnsLength, count, pageNumber, statisticsFolder]
  )

  const getTableColumns = useMemo(() => {
    let allColumns = {
      ...getBaseColumns({ onSelectRow: handleSelectRow, isAdmin: true }),
      ...getColumns()
    }
    return [
      allColumns["created_timestamp"],
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data })),
      getActionColumn({ onSelectRow: handleSelectRow })
    ]
  }, [myColumnsHash])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (type === "changeVisible") {
      props.changeVisibleFilters("visibleFiltersAdmin", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
  }, [])

  const handleChangeColumns = useCallback((newColumns) => {
    props.changeColumns("columnsAdmin", newColumns)
  }, [])

  function handleSelectRow(data, type) {}

  return (
    <div className="requests-page">
      <Flex gap={32} align="center" justify="space-between" style={{ margin: "0 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          //filterOptions={filterOptions}
          allFilters={allFilters}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          filtersType={"gifts"} //for rename some fields labels in filtersTypeByIdMap
          showClearBtn={!isFiltersDefault}
          onClearFilters={props.clearFilters}
        />

        <Space style={{ flexShrink: 0 }}>
          <GlobalFilterTestData setPageNumber={setPageNumber} />

          <ManageColumn
            myColumns={myColumns}
            onChangeColumns={handleChangeColumns}
            pinnedColumns={pinnedColumns}
          />
        </Space>
      </Flex>

      <Table
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        //withOutPagination={true}
        locale={tableSettings.locale}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  businessAccountId: state.auth.userInfo.current_business_account_id,
  isMobile: state.nav.isMobile,

  visibleFiltersAdmin: state.requestsGifts.visibleFiltersAdmin,

  defaultColumnsAdmin: state.requestsGifts.defaultColumnsAdmin,

  columnsAdmin: state.requestsGifts.columnsAdmin,

  fetching: state.requestsGifts.fetching,
  result: state.requestsGifts.result,

  filters: state.requestsGifts.filters,
  filterOptions: state.requestsGifts.filterOptions,

  statistics: state.businessAccounts.statistics,
  test_data: state.globalFilters.testData
})

const mapDispatchToProps = (dispatch) => ({
  getInfo: (params) => dispatch(getRequestsGifts(params)),

  changeColumns: (key, columns) =>
    dispatch(requestsGiftsActions.requestsGiftsSetProp({ key, value: columns })),
  changeFilters: (filters) =>
    dispatch(requestsGiftsActions.requestsGiftsSetProp({ key: "filters", value: filters })),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(
      requestsGiftsActions.requestsGiftsSetProp({ key: visibleFiltersType, value: filters })
    ),
  clearFilters: () => dispatch(requestsGiftsActions.clearRequestsGiftsFilters())
})

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(RequestsGiftsPage))
